import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { Application } from '@/types';

/** Keep cache-only list rows after refetch until server list order catches up. */
/** Same window as PIPELINE_IN_FLIGHT_GRACE_MS in pendingProcessing.ts (no import — avoids circular deps). */
export const RECENT_LIST_OVERLAY_MS = 12 * 60 * 1000;

export type CompanyApplicationsPage = {
  items: Application[];
  hasMore: boolean;
  nextCursor?: string;
};

export type CompanyApplicationsOrderBy = 'appliedAt' | 'updatedAt';

/** API page size (UI table stays at 20 rows; each fetch covers ~2–3 table pages). */
export const COMPANY_APPLICATIONS_FETCH_SIZE = 50;

/** Warm-up fetches after first load so early table pages do not wait on the network. */
export const COMPANY_APPLICATIONS_INITIAL_PREFETCH_PAGES = 2;

/** Server-side list scope for company applications infinite query. */
export type CompanyApplicationsListScope = {
  archived: boolean;
  state?: string;
  jobPositionId?: string;
  orderBy: CompanyApplicationsOrderBy;
};

/** Shared warm scope: dashboard + Applications default tab (same query key, no legacy hydrate). */
export const COMPANY_APPLICATIONS_WARM_LIST_SCOPE: CompanyApplicationsListScope = {
  archived: false,
  orderBy: 'updatedAt',
};

export function buildCompanyApplicationsListScope(input: {
  archived: boolean;
  statusFilter: string;
  positionFilter: string;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
}): CompanyApplicationsListScope {
  return {
    archived: input.archived,
    state: serverStateFromStatusFilter(input.statusFilter),
    jobPositionId: input.positionFilter !== 'all' ? input.positionFilter : undefined,
    orderBy: resolveServerListOrderBy(input.sortColumn),
  };
}

/** True when list API should pass filters (status, position). PIPELINE_STUCK stays client-only. */
export function scopeUsesServerFilters(scope: CompanyApplicationsListScope): boolean {
  return Boolean(scope.state || scope.jobPositionId);
}

/** Default Applications tab: all active, server Updated desc — eligible for position-head warm. */
export function isCompanyApplicationsWarmScope(scope: CompanyApplicationsListScope): boolean {
  return !scope.archived && !scope.state && !scope.jobPositionId && scope.orderBy === 'updatedAt';
}

export function applicationMatchesServerScope(
  app: Pick<Application, 'state' | 'positionId'>,
  scope: CompanyApplicationsListScope,
): boolean {
  if (scope.state && app.state !== scope.state) return false;
  if (scope.jobPositionId && app.positionId !== scope.jobPositionId) return false;
  return true;
}

/**
 * Union of all `applications-list` pages for this company + tab, filtered to match scope.
 * Used for instant UI while a scoped query loads; does not drive pagination cursors.
 */
export function collectApplicationsFromListCaches(
  queryClient: QueryClient,
  companyId: string,
  scope: CompanyApplicationsListScope,
): Application[] {
  const entries = queryClient.getQueriesData<InfiniteData<CompanyApplicationsPage>>({
    queryKey: ['applications-list', companyId],
  });

  const byId = new Map<string, Application>();
  for (const [key, data] of entries) {
    if (!data?.pages?.length) continue;
    const keyScope = (key as readonly unknown[])[2] as CompanyApplicationsListScope | undefined;
    if (!keyScope || keyScope.archived !== scope.archived) continue;
    if (keyScope.orderBy !== scope.orderBy) continue;
    for (const app of flattenApplicationPages(data.pages)) {
      if (!applicationMatchesServerScope(app, scope)) continue;
      const prev = byId.get(app.id);
      byId.set(app.id, prev ? preferNewerApplicationRow(prev, app) : app);
    }
  }

  return Array.from(byId.values());
}

/** True for per-position slices `['applications', companyId, positionId, archived]`. */
function isPositionApplicationsQueryKey(
  key: readonly unknown[],
  companyId: string,
): key is readonly ['applications', string, string, boolean] {
  return (
    key.length === 4
    && key[0] === 'applications'
    && key[1] === companyId
    && typeof key[2] === 'string'
    && typeof key[3] === 'boolean'
  );
}

/**
 * Union per-position application caches (Job Position tab) — often fresher than company list pages.
 */
export function collectApplicationsFromPositionCaches(
  queryClient: QueryClient,
  companyId: string,
  scope: CompanyApplicationsListScope,
): Application[] {
  const entries = queryClient.getQueriesData<Application[]>({
    queryKey: ['applications', companyId],
  });

  const byId = new Map<string, Application>();
  for (const [key, data] of entries) {
    if (!data?.length || !isPositionApplicationsQueryKey(key, companyId)) continue;
    if (key[3] !== scope.archived) continue;
    if (scope.jobPositionId && key[2] !== scope.jobPositionId) continue;
    for (const app of data) {
      if (!applicationMatchesServerScope(app, scope)) continue;
      const prev = byId.get(app.id);
      byId.set(app.id, prev ? preferNewerApplicationRow(prev, app) : app);
    }
  }

  return Array.from(byId.values());
}

/**
 * Merge infinite-query pages with list-cache overlays (detail/bulk patches, other scopes).
 * Prevents freshly processed rows from vanishing when a stale `applications-list` refetch
 * returns before Firestore list order catches up.
 */
export function mergeCompanyApplicationsListSources(
  queryClient: QueryClient,
  companyId: string,
  scope: CompanyApplicationsListScope,
  pages: CompanyApplicationsPage[] | undefined,
  legacyOverlay?: Application[] | undefined,
  sessionOverlay?: Application[] | undefined,
): Application[] {
  const fromQuery = flattenApplicationPages(pages);
  const fromCaches = collectApplicationsFromListCaches(queryClient, companyId, scope);
  const fromPositions = collectApplicationsFromPositionCaches(queryClient, companyId, scope);
  const fromLegacy = (legacyOverlay ?? []).filter((app) =>
    applicationMatchesServerScope(app, scope),
  );
  const fromSession = (sessionOverlay ?? []).filter((app) =>
    applicationMatchesServerScope(app, scope),
  );
  return mergeApplicationListsPreferNewest(
    fromQuery,
    fromCaches,
    fromPositions,
    fromLegacy,
    fromSession,
  );
}

/**
 * Rows to merge into the first server page when the infinite query has no pages yet
 * (session overlay, legacy company slice, other scopes, position caches).
 */
export function collectCompanyListPageOverlay(
  queryClient: QueryClient,
  companyId: string,
  scope: CompanyApplicationsListScope,
): Application[] {
  const legacy = scope.archived
    ? (queryClient.getQueryData<Application[]>(
      ['applications', companyId, true],
    ) ?? []).filter((app) => applicationMatchesServerScope(app, scope))
    : [];
  return mergeApplicationListsPreferNewest(
    legacy,
    collectApplicationsFromListCaches(queryClient, companyId, scope),
    collectApplicationsFromPositionCaches(queryClient, companyId, scope),
  );
}

/** First `applications-list` fetch: keep recent patches missing from the server page. */
export function mergeFreshCompanyListPage(
  queryClient: QueryClient,
  companyId: string,
  scope: CompanyApplicationsListScope,
  freshPage: CompanyApplicationsPage,
  nowMs = Date.now(),
  mergeOptions?: MergeServerListPageOptions,
): CompanyApplicationsPage {
  const overlay = collectCompanyListPageOverlay(queryClient, companyId, scope);
  return {
    ...freshPage,
    items: mergeServerListPageWithOverlay(freshPage.items, overlay, nowMs, mergeOptions),
  };
}

export type MergeServerListPageOptions = {
  /** Session-settled ids — keep in page merge regardless of 12m overlay window. */
  retainCacheOnlyIds?: ReadonlySet<string>;
};

export function mergeServerListPageWithOverlay(
  serverItems: Application[],
  cachedItems: Application[],
  nowMs = Date.now(),
  options?: MergeServerListPageOptions,
): Application[] {
  const retain = options?.retainCacheOnlyIds;
  const byId = new Map<string, Application>();
  for (const app of serverItems) {
    byId.set(app.id, app);
  }
  for (const cached of cachedItems) {
    const existing = byId.get(cached.id);
    if (existing) {
      byId.set(cached.id, preferNewerApplicationRow(existing, cached));
      continue;
    }
    const keepBySession = retain?.has(cached.id) ?? false;
    if (
      keepBySession
      || nowMs - applicationRowFreshnessMs(cached) <= RECENT_LIST_OVERLAY_MS
    ) {
      byId.set(cached.id, cached);
    }
  }
  return Array.from(byId.values());
}

/** Maps UI status filter to BE `state` query (PIPELINE_STUCK is client-only). */
export function serverStateFromStatusFilter(statusFilter: string): string | undefined {
  if (statusFilter === 'all' || statusFilter === 'PIPELINE_STUCK') return undefined;
  return statusFilter;
}

/**
 * Maps the active table sort column to the list API `orderBy` field.
 * Default is `updatedAt` (most recently changed first — processing, new applications).
 * Use `appliedAt` when the user sorts by the Applied column.
 */
export function resolveServerListOrderBy(sortColumn: string): CompanyApplicationsOrderBy {
  return sortColumn === 'appliedAt' ? 'appliedAt' : 'updatedAt';
}

/** List sort columns whose order comes from the server (`orderBy`), not client re-sort. */
export function isServerAlignedListSortColumn(column: string): boolean {
  return column === 'updatedAt' || column === 'appliedAt';
}

/** ISO string or legacy Firestore timestamp shape on list/detail payloads. */
export type ApplicationTimestampLike =
  | string
  | { _seconds: number; _nanoseconds?: number }
  | undefined;

export function timestampMs(value: ApplicationTimestampLike): number | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'object' && '_seconds' in value) {
    return value._seconds * 1000 + Math.floor((value._nanoseconds ?? 0) / 1e6);
  }
  return null;
}

/** Recency for dedup — matches Updated sort fallback (`updatedAt` → `appliedAt`). */
export function applicationRowFreshnessMs(
  app: Pick<Application, 'updatedAt' | 'appliedAt'>,
): number {
  return timestampMs(app.updatedAt) ?? timestampMs(app.appliedAt) ?? 0;
}

type InterviewRow = NonNullable<Application['interviews']>[number];

/** When the same pipeline run appears twice, prefer the more advanced terminal status (e.g. Cancelled over stale Scheduled). */
const INTERVIEW_MERGE_STATUS_RANK: Record<InterviewRow['status'], number> = {
  Scheduled: 1,
  Updated: 2,
  Completed: 3,
  Cancelled: 4,
};

function mergeInterviewPair(prev: InterviewRow, row: InterviewRow): InterviewRow {
  const prevCreated = prev.createdAt ? Date.parse(prev.createdAt) : 0;
  const nextCreated = row.createdAt ? Date.parse(row.createdAt) : 0;
  let merged = nextCreated >= prevCreated ? { ...prev, ...row } : { ...row, ...prev };

  const prevRank = INTERVIEW_MERGE_STATUS_RANK[prev.status] ?? 0;
  const rowRank = INTERVIEW_MERGE_STATUS_RANK[row.status] ?? 0;
  if (prevRank !== rowRank) {
    const dominant = prevRank > rowRank ? prev : row;
    const other = prevRank > rowRank ? row : prev;
    merged = { ...other, ...dominant, status: dominant.status };
  }
  return merged;
}

function mergeInterviewArrays(
  primary: Application['interviews'] | undefined,
  fallback: Application['interviews'] | undefined,
): Application['interviews'] | undefined {
  const rows = [...(fallback ?? []), ...(primary ?? [])];
  if (rows.length === 0) return undefined;

  const byRunId = new Map<string, InterviewRow>();
  for (const row of rows) {
    const key = row.pipelineRunId ?? row.id;
    const prev = byRunId.get(key);
    byRunId.set(key, prev ? mergeInterviewPair(prev, row) : row);
  }
  return Array.from(byRunId.values());
}

/** Union interview rows by pipelineRunId — used by list cache merge and live sync. */
export function mergeApplicationInterviews(
  primary: Application['interviews'] | undefined,
  fallback: Application['interviews'] | undefined,
): Application['interviews'] | undefined {
  return mergeInterviewArrays(primary, fallback);
}

function mergeApplicationDisplayFields(
  primary: Application,
  fallback: Application,
): Application {
  const positionId =
    primary.positionId
    || (primary as Application & { jobPositionId?: string }).jobPositionId
    || fallback.positionId
    || (fallback as Application & { jobPositionId?: string }).jobPositionId
    || '';

  const archived =
    primary.archived === true
      ? true
      : primary.archived === false
        ? false
        : fallback.archived;

  const interviews = mergeInterviewArrays(primary.interviews, fallback.interviews);

  const hasScheduledInterview =
    interviews?.some((i) => i.status !== 'Cancelled')
    ?? primary.hasScheduledInterview
    ?? fallback.hasScheduledInterview;

  return {
    ...fallback,
    ...primary,
    positionId,
    candidateFirstName: primary.candidateFirstName ?? fallback.candidateFirstName,
    candidateLastName: primary.candidateLastName ?? fallback.candidateLastName,
    candidateEmail: primary.candidateEmail ?? fallback.candidateEmail,
    positionTitle: primary.positionTitle || fallback.positionTitle,
    channel: primary.channel ?? fallback.channel,
    appliedAt: primary.appliedAt ?? fallback.appliedAt,
    updatedAt: primary.updatedAt ?? fallback.updatedAt,
    ...(interviews?.length ? { interviews } : {}),
    hasScheduledInterview,
    ...(archived !== undefined ? { archived } : {}),
  };
}

/** When the same id appears in multiple list pages/caches, keep fresher timestamps without dropping fields from partial poll patches. */
export function preferNewerApplicationRow(
  existing: Application,
  incoming: Application,
): Application {
  return applicationRowFreshnessMs(incoming) >= applicationRowFreshnessMs(existing)
    ? mergeApplicationDisplayFields(incoming, existing)
    : mergeApplicationDisplayFields(existing, incoming);
}

/** Bulk-status / poll payloads often omit candidate fields — do not insert these into list pages. */
export function isSparseApplicationListRow(app: Application): boolean {
  const hasName =
    Boolean(app.candidateFirstName?.trim())
    || Boolean(app.candidateLastName?.trim());
  const hasEmail = Boolean(app.candidateEmail?.trim());
  return !hasName && !hasEmail;
}

/** Union list slices deduped by id; newer `updatedAt` wins (cursor pagination + cache patch). */
export function mergeApplicationListsPreferNewest(
  ...lists: Application[][]
): Application[] {
  const byId = new Map<string, Application>();
  for (const list of lists) {
    for (const app of list) {
      const prev = byId.get(app.id);
      byId.set(app.id, prev ? preferNewerApplicationRow(prev, app) : app);
    }
  }
  return Array.from(byId.values());
}

/** Client-side sort key; `updatedAt` falls back to `appliedAt` for legacy rows. */
export function getApplicationListSortValue(
  app: Pick<Application, 'updatedAt' | 'appliedAt'>,
  column: string,
): unknown {
  if (column === 'updatedAt') {
    return timestampMs(app.updatedAt) ?? timestampMs(app.appliedAt) ?? 0;
  }
  if (column === 'appliedAt') {
    return timestampMs(app.appliedAt) ?? 0;
  }
  return (app as Record<string, unknown>)[column];
}

export function flattenApplicationPages(
  pages: { items: Application[] }[] | undefined,
): Application[] {
  if (!pages?.length) return [];
  const byId = new Map<string, Application>();
  for (const page of pages) {
    for (const app of page.items) {
      const prev = byId.get(app.id);
      byId.set(app.id, prev ? preferNewerApplicationRow(prev, app) : app);
    }
  }
  return Array.from(byId.values());
}

/** Re-sort loaded rows to match server `orderBy` (patches do not move rows across pages). */
export function orderApplicationsForServerList(
  items: Application[],
  orderBy: CompanyApplicationsOrderBy,
): Application[] {
  return [...items].sort((a, b) => {
    const aMs = orderBy === 'appliedAt'
      ? timestampMs(a.appliedAt) ?? 0
      : applicationRowFreshnessMs(a);
    const bMs = orderBy === 'appliedAt'
      ? timestampMs(b.appliedAt) ?? 0
      : applicationRowFreshnessMs(b);
    return bMs - aMs;
  });
}

